import React, { useEffect, useState } from 'react';
import { LuSailboat } from '@react-icons/all-files/lu/LuSailboat';
import { CiCalendar } from '@react-icons/all-files/ci/CiCalendar';
import { PiShoppingCartThin } from '@react-icons/all-files/pi/PiShoppingCartThin';
import { CiHeart } from '@react-icons/all-files/ci/CiHeart';
import { CiHeadphones } from '@react-icons/all-files/ci/CiHeadphones';
import { Avatar, formattingHelpers, toast } from 'day8-ui';
import { ContactButton } from 'day8-react/build/new-components';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useSelector } from 'react-redux';
import history from '../../history';
import { useBookingBasket } from '../../context/BookingBasketContext';
import { useMobileAppNav } from '../../context/MobileAppNavContext';
import BookingTimeout from '../booking/BookingTimeout';

const AUTH_PATHS = /^\/account\/(login|signup|reset-password|recover-password)/;

const isAccountRoute = path =>
  /^\/account(\/|$)/.test(path) && !AUTH_PATHS.test(path);

/**
 * Rendered height of the fixed bar. Single source of truth: the spacer that
 * keeps content clear of it and the Intercom launcher offset both derive from
 * this, and it is mirrored into the --yw-tabbar CSS variable.
 * Labels are kept on one line so the bar cannot outgrow it.
 */
export const TAB_BAR_HEIGHT = 80;

const tabBtnClass = selected =>
  `dui-py-2 dui-px-4 dui-font-medium dui-flex dui-flex-col dui-items-center dui-justify-center dui-gap-2 dui-cursor-pointer dui-rounded-md dui-whitespace-nowrap${selected ? ' dui-bg-primary-950 dui-text-white' : ''}`;

const MobileAppTabBar = ({ isYachtsAndFriends }) => {
  const loggedUser = useSelector(state => state.user.loggedUser);
  const isFunnelYachtsRedirectFeature = useFeatureIsOn('custom-yachts-redirect');
  const { shouldShowCheckout } = useBookingBasket();
  const { openDrawer } = useMobileAppNav();
  const [currentPath, setCurrentPath] = useState(history.location.pathname);

  useEffect(() => {
    const unlisten = history.listen(location => setCurrentPath(location.pathname));
    return unlisten;
  }, []);

  const showPlans = !isYachtsAndFriends;
  const bookPaths = isYachtsAndFriends? ['/browse/yachtsandfriends']: ['/browse', '/browse/yachts'];
  const isBookSelected = bookPaths.includes(currentPath);
  const isPlanSelected = /^\/plan(\/|$)/.test(currentPath);
  const isBasketSelected = currentPath === '/booking/payment';
  const isAccountSelected = isAccountRoute(currentPath);

  const browseHref = isYachtsAndFriends? '/browse/yachtsandfriends': `/browse${isFunnelYachtsRedirectFeature ? '/yachts' : ''}`;

  const fbImageUrl = loggedUser?.fbUid && `https://graph.facebook.com/v2.9/${loggedUser.fbUid}/picture?type=square`;
  const profileImageUrl = loggedUser? (loggedUser.profilePicture || fbImageUrl): null;

  return (
    <div className="md:dui-hidden dui-fixed dui-bottom-0 dui-bg-white dui-z-10 dui-w-full dui-border-t dui-border-solid dui-border-gray-200">
      <div className="dui-container dui-mx-auto dui-py-2">
        <div className="dui-flex dui-items-center dui-justify-between">
          <button
            type="button"
            className={tabBtnClass(isBookSelected)}
            onClick={() => history.push(browseHref)}
          >
            <CiCalendar className="dui-text-2xl" />
            <p className="dui-text-xs">Book</p>
          </button>

          {showPlans && (
            <button
              type="button"
              className={tabBtnClass(isPlanSelected)}
              onClick={() => history.push(isYachtsAndFriends ? '/plan/yachtsandfriends' : '/plan')}
            >
              <CiHeart className="dui-text-2xl" />
              {/* No plan count here: "Saved (12)" wraps to a second line and
                  grows the bar past TAB_BAR_HEIGHT. The count still shows in
                  the desktop navbar dropdown, where there is room for it. */}
              <p className="dui-text-xs">Saved</p>
            </button>
          )}

          <button
            type="button"
            className={`${shouldShowCheckout && 'dui-min-w-[67px]'} dui-relative ${tabBtnClass(isBasketSelected)}`}
            onClick={() => {
              if (shouldShowCheckout) {
                history.push('/booking/payment');
              } else {
                toast.warning('You have no items in your basket');
              }
            }}
          >
            <div className="dui-relative">
              <PiShoppingCartThin className="dui-text-2xl" />
              {shouldShowCheckout && (
                <LuSailboat className="dui-absolute dui-text-sm dui-top-0 dui-left-[20%] dui-rotate-[-45deg] dui-text-gray-400" />
              )}
              {shouldShowCheckout && (
                <div className="dui-absolute dui-top-0 dui-right-0 dui-w-2 dui-h-2 dui-bg-green-600 dui-rounded-full" />
              )}
            </div>
            <p className="dui-text-xs">{shouldShowCheckout ? <BookingTimeout /> : 'Basket'}</p>
          </button>

          <ContactButton
            brand={isYachtsAndFriends ? 'ynf' : 'tyw'}
            trigger={(
              <button type="button" className={tabBtnClass(false)}>
                <CiHeadphones className="dui-text-2xl" />
                <p className="dui-text-xs">S.O.S.</p>
              </button>
            )}
          />

          <button
            type="button"
            className={tabBtnClass(isAccountSelected)}
            onClick={() => openDrawer()}
          >
            <Avatar avatarSize="SMALL" className="!dui-w-6 !dui-h-6 dui-text-xs">
              {profileImageUrl && (
                <img src={profileImageUrl} alt={loggedUser?.name || 'Account'} className="dui-w-full dui-h-full dui-object-cover" />
              )}
              {!profileImageUrl && loggedUser && (
                <span>{formattingHelpers.getInitialsFromName(loggedUser.firstName, loggedUser.lastName)}</span>
              )}
              {!profileImageUrl && !loggedUser && (
                <img src="https://cdn.theyachtweek.com/assets/icons/user-icon.png" alt="Account" className="dui-w-full dui-h-full dui-object-cover" />
              )}
            </Avatar>
            <p className="dui-text-xs">Account</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MobileAppTabBar;

export { AUTH_PATHS, isAccountRoute };
